/**
 * TanStack Query hooks for the partners feature. Keys are namespaced
 * "partners/…"; the party collection rides the shared cursor helpers over the
 * `{ items, next_cursor }` keyset envelope, and the bounded per-party
 * sub-collections (addresses, contacts, channel identities) plus the three
 * reference lists are drained through `collectCursorPages`.
 */
import { useQuery } from "@tanstack/react-query";
import {
  useCursorList,
  type CursorQueryResult,
  type ListFilters,
  type SortState,
} from "@/features/shared/ops-api";

import {
  getParty,
  listChannelIdentities,
  listCustomerGroups,
  listParties,
  listPartyAddresses,
  listPartyContacts,
  listPaymentTerms,
  listSupplierGroups,
  type ChannelIdentity,
  type CustomerGroup,
  type Party,
  type PartyAddress,
  type PartyContact,
  type PartyKind,
  type PartyRole,
  type PartyStatus,
  type PaymentTerm,
  type SupplierGroup,
} from "./api";

export const partnersKeys = {
  parties: {
    all: ["partners/parties"] as const,
    list: (filters: Record<string, unknown>) => ["partners/parties", "list", filters] as const,
    detail: (partyId: string) => ["partners/parties", "detail", partyId] as const,
    addresses: (partyId: string, includeArchived: boolean) =>
      ["partners/parties", "addresses", partyId, includeArchived] as const,
    contacts: (partyId: string, includeArchived: boolean) =>
      ["partners/parties", "contacts", partyId, includeArchived] as const,
    channelIdentities: (partyId: string) =>
      ["partners/parties", "channel-identities", partyId] as const,
  },
  customerGroups: {
    all: ["partners/customer-groups"] as const,
    list: () => ["partners/customer-groups", "list"] as const,
  },
  supplierGroups: {
    all: ["partners/supplier-groups"] as const,
    list: () => ["partners/supplier-groups", "list"] as const,
  },
  paymentTerms: {
    all: ["partners/payment-terms"] as const,
    list: () => ["partners/payment-terms", "list"] as const,
  },
};

// ── Parties ──────────────────────────────────────────────────────────────

export interface PartyListFilters extends ListFilters {
  q?: string;
  kind?: PartyKind;
  status?: PartyStatus;
  role?: PartyRole;
}

export function usePartyList(
  filters: PartyListFilters,
  sort: SortState,
  limit?: number,
): CursorQueryResult<Party> {
  return useCursorList(
    partnersKeys.parties.list({ ...filters, sort: sort?.key, dir: sort?.dir, limit }),
    ({ cursor, limit: pageLimit, sort: pageSort, signal }) =>
      listParties(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
          ...(pageSort === null || pageSort === undefined
            ? {}
            : { sort: `${pageSort.key}:${pageSort.dir}` }),
        },
        signal,
      ),
    { sort, limit },
  );
}

/**
 * Party aggregate + its strong ETag. The ETag is the If-Match validator for
 * the header PATCH, for the archive/unarchive transitions, and for the FIRST
 * profile PUT (before a profile-scoped validator exists).
 */
export type PartySnapshot = Awaited<ReturnType<typeof getParty>>;

export function usePartyDetail(partyId: string | undefined) {
  return useQuery({
    queryKey: partnersKeys.parties.detail(partyId ?? ""),
    queryFn: (): Promise<PartySnapshot> => getParty(partyId as string),
    enabled: partyId !== undefined,
  });
}

export function usePartyAddresses(
  partyId: string | undefined,
  includeArchived: boolean,
  enabled = true,
) {
  return useQuery<PartyAddress[]>({
    queryKey: partnersKeys.parties.addresses(partyId ?? "", includeArchived),
    queryFn: ({ signal }) =>
      listPartyAddresses(partyId as string, { include_archived: includeArchived }, signal),
    enabled: partyId !== undefined && enabled,
  });
}

export function usePartyContacts(
  partyId: string | undefined,
  includeArchived: boolean,
  enabled = true,
) {
  return useQuery<PartyContact[]>({
    queryKey: partnersKeys.parties.contacts(partyId ?? "", includeArchived),
    queryFn: ({ signal }) =>
      listPartyContacts(partyId as string, { include_archived: includeArchived }, signal),
    enabled: partyId !== undefined && enabled,
  });
}

export function useChannelIdentities(partyId: string | undefined, enabled = true) {
  return useQuery<ChannelIdentity[]>({
    queryKey: partnersKeys.parties.channelIdentities(partyId ?? ""),
    queryFn: ({ signal }) => listChannelIdentities(partyId as string, signal),
    enabled: partyId !== undefined && enabled,
  });
}

// ── Reference data ───────────────────────────────────────────────────────

export function useCustomerGroups(enabled = true) {
  return useQuery<CustomerGroup[]>({
    queryKey: partnersKeys.customerGroups.list(),
    queryFn: ({ signal }) => listCustomerGroups({}, signal),
    enabled,
  });
}

export function useSupplierGroups(enabled = true) {
  return useQuery<SupplierGroup[]>({
    queryKey: partnersKeys.supplierGroups.list(),
    queryFn: ({ signal }) => listSupplierGroups({}, signal),
    enabled,
  });
}

export function usePaymentTerms(enabled = true) {
  return useQuery<PaymentTerm[]>({
    queryKey: partnersKeys.paymentTerms.list(),
    queryFn: ({ signal }) => listPaymentTerms({}, signal),
    enabled,
  });
}
